/**
 * ManagerPinSection — Owner-facing block to set, change, or clear the
 * manager PIN. Rendered on BOTH DashSettings and DashAdmin so owners can
 * find it in either place. The PIN itself gates entry to those pages when
 * one is set — no PIN, no wall.
 */
import { useEffect, useState } from "react";
import { Lock, Trash, CheckCircle, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ManagerPinSection() {
  const [isSet, setIsSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  // Clear-PIN modal state — replaces window.prompt() which Safari + several
  // SPA-embedded contexts silently swallow.
  const [clearOpen, setClearOpen] = useState(false);
  const [clearPin, setClearPin] = useState("");
  const [clearErr, setClearErr] = useState("");

  const load = () => {
    api.get("/manager-pin/status").then((r) => { setIsSet(!!r.data.is_set); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (!/^\d+$/.test(next) || next.length < 4 || next.length > 8) return toast.error("PIN must be 4–8 digits.");
    if (next !== confirm) return toast.error("PINs don't match.");
    setBusy(true);
    try {
      await api.post("/manager-pin/set", { pin: next, current_pin: current || undefined });
      toast.success(isSet ? "PIN changed." : "PIN set. Critical toggles are now protected.");
      setCurrent(""); setNext(""); setConfirm(""); setOpenForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    }
    setBusy(false);
  };

  const openClear = () => {
    setClearPin("");
    setClearErr("");
    setClearOpen(true);
  };

  const doClear = async () => {
    if (!clearPin || clearPin.length < 4) { setClearErr("Enter the current 4–8 digit PIN."); return; }
    setBusy(true);
    setClearErr("");
    try {
      await api.delete(`/manager-pin?current_pin=${encodeURIComponent(clearPin)}`);
      toast.success("Manager PIN cleared. All toggles are open again.");
      setClearOpen(false);
      setClearPin("");
      load();
    } catch (e) {
      setClearErr(e.response?.data?.detail || "Clear failed.");
    }
    setBusy(false);
  };

  if (loading) return null;

  return (
    <div className="border-t border-[#E1DFD5] pt-4" data-testid="manager-pin-section">
      <div className="overline mb-2 flex items-center gap-2">
        <Lock size={14} weight="fill" color="#D96C5B" /> Manager PIN
      </div>
      <p className="text-xs text-[#5C6B61] leading-snug mb-3">
        Set a 4–8 digit PIN and staff will have to enter it before turning the AI receptionist off, changing manager phones, or entering the Admin / Settings pages. Owners who know the PIN stay unlocked for 5 minutes at a time.
        {isSet ? " A PIN is set on this account." : " No PIN is currently set — anyone with dashboard access can toggle everything."}
      </p>
      {!openForm && (
        <div className="flex gap-2 flex-wrap items-center">
          <button
            type="button"
            onClick={() => setOpenForm(true)}
            className="text-sm font-semibold px-4 py-2 rounded-full bg-[#1E3A2F] text-white hover:bg-[#2b5648]"
            data-testid="pin-open-form"
          >
            {isSet ? "Change PIN" : "Set a PIN"}
          </button>
          {isSet && (
            <button
              type="button"
              onClick={openClear}
              disabled={busy}
              className="text-sm font-semibold px-4 py-2 rounded-full border border-[#D96C5B]/40 text-[#D96C5B] hover:bg-[#D96C5B]/10 inline-flex items-center gap-1.5"
              data-testid="pin-clear"
            >
              <Trash size={14} weight="fill" /> Remove PIN
            </button>
          )}
          {isSet && (
            <div className="text-xs text-[#1E3A2F] font-semibold inline-flex items-center gap-1.5 py-2">
              <CheckCircle size={14} weight="fill" color="#1E3A2F" /> Active
            </div>
          )}
        </div>
      )}
      {openForm && (
        <div className="space-y-3 max-w-sm">
          {isSet && (
            <div>
              <label className="block text-xs font-semibold text-[#121914] mb-1">Current PIN</label>
              <input
                type="password" inputMode="numeric" maxLength={8}
                value={current}
                onChange={(e) => setCurrent(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") submit(e); }}
                className="input w-full"
                data-testid="pin-current"
                required
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-[#121914] mb-1">New PIN (4–8 digits)</label>
            <input
              type="password" inputMode="numeric" maxLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") submit(e); }}
              className="input w-full"
              data-testid="pin-new"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#121914] mb-1">Confirm new PIN</label>
            <input
              type="password" inputMode="numeric" maxLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") submit(e); }}
              className="input w-full"
              data-testid="pin-confirm"
              required
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={submit} disabled={busy} className="btn-primary text-sm !py-2 !px-4" data-testid="pin-save">
              {busy ? "Saving…" : "Save PIN"}
            </button>
            <button type="button" onClick={() => { setOpenForm(false); setCurrent(""); setNext(""); setConfirm(""); }} className="text-sm font-semibold px-4 py-2 rounded-full text-[#5C6B61] hover:bg-[#E1DFD5]">
              Cancel
            </button>
          </div>
        </div>
      )}

      {clearOpen && (
        <div
          className="fixed inset-0 z-[70] bg-[#121914]/70 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="pin-clear-modal"
          onClick={() => !busy && setClearOpen(false)}
        >
          <div className="bg-[#F7F6F2] rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D96C5B] flex items-center justify-center">
                  <Trash size={20} color="#fff" weight="fill" />
                </div>
                <div>
                  <div className="font-heading font-extrabold text-lg text-[#121914]">Remove manager PIN?</div>
                  <div className="text-xs text-[#5C6B61] mt-0.5">Enter the current PIN to confirm.</div>
                </div>
              </div>
              <button type="button" onClick={() => setClearOpen(false)} disabled={busy} className="text-[#5C6B61] hover:text-[#121914]" data-testid="pin-clear-cancel">
                <X size={20} weight="bold" />
              </button>
            </div>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              maxLength={8}
              value={clearPin}
              onChange={(e) => setClearPin(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") doClear(); }}
              placeholder="Current 4–8 digit PIN"
              className="input w-full text-2xl tracking-widest text-center"
              data-testid="pin-clear-input"
            />
            {clearErr && <div className="text-xs text-[#D96C5B] font-semibold mt-2" data-testid="pin-clear-err">{clearErr}</div>}
            <p className="text-xs text-[#5C6B61] mt-3 leading-snug">
              Once cleared, the Admin and Settings pages open without a PIN and every unsafe toggle is unlocked for anyone with dashboard access.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setClearOpen(false)} disabled={busy} className="text-sm font-semibold text-[#5C6B61] px-4 py-2 rounded-full hover:bg-[#E1DFD5]">
                Cancel
              </button>
              <button
                type="button"
                onClick={doClear}
                disabled={busy || clearPin.length < 4}
                className="text-sm font-semibold px-4 py-2 rounded-full bg-[#D96C5B] text-white hover:bg-[#c05a4a] disabled:opacity-50"
                data-testid="pin-clear-confirm"
              >
                {busy ? "Removing…" : "Remove PIN"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
